import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { CampaignManagementService } from '../../../services/campaignManagementService';

const CampaignTimeline = ({ timelineData, isLoading, className = '' }) => {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [timelineView, setTimelineView] = useState('milestones'); // milestones, performance, events
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load campaigns from Supabase
  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const { data: campaignsData, error: campaignError } = await CampaignManagementService?.getCampaigns();
      
      if (campaignError) {
        setError(campaignError);
        setCampaigns([]);
      } else {
        setCampaigns(campaignsData || []);
        // Set first campaign as selected if available
        if (campaignsData?.length > 0 && !selectedCampaign) {
          setSelectedCampaign(campaignsData?.[0]?.id);
        }
      }
    } catch (error) {
      setError('Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // Generate timeline milestones from real campaign data
  const generateMilestonesFromCampaign = (campaign) => {
    if (!campaign) return [];

    const milestones = [];
    
    // Campaign creation milestone
    milestones?.push({
      id: `${campaign?.id}-created`,
      date: campaign?.createdAt?.toISOString()?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
      type: 'start',
      title: 'Campaign Created',
      description: `${campaign?.name} campaign was created and configured`,
      status: 'completed',
      metrics: { scans: 0, conversions: 0 }
    });

    // Campaign start milestone (if start date exists)
    if (campaign?.startDate) {
      milestones?.push({
        id: `${campaign?.id}-started`,
        date: campaign?.startDate?.toISOString()?.split('T')?.[0],
        type: 'start',
        title: 'Campaign Launch',
        description: `${campaign?.name} campaign officially launched`,
        status: campaign?.status === 'draft' ? 'pending' : 'completed',
        metrics: { scans: 0, conversions: 0 }
      });
    }

    // QR codes milestone
    if (campaign?.qrCodes?.length > 0) {
      milestones?.push({
        id: `${campaign?.id}-qrs`,
        date: campaign?.createdAt?.toISOString()?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
        type: 'milestone',
        title: 'QR Codes Generated',
        description: `${campaign?.qrCodes?.length} QR code${campaign?.qrCodes?.length > 1 ? 's' : ''} created and deployed`,
        status: 'completed',
        metrics: { 
          scans: campaign?.analytics?.totalScans || 0, 
          conversions: campaign?.analytics?.totalConversions || 0 
        }
      });
    }

    // Performance milestones based on actual analytics
    if (campaign?.analytics?.totalScans > 0) {
      milestones?.push({
        id: `${campaign?.id}-performance`,
        date: new Date()?.toISOString()?.split('T')?.[0], // Current date for now
        type: 'milestone',
        title: `${campaign?.analytics?.totalScans} Scans Achieved`,
        description: `Campaign has reached ${campaign?.analytics?.totalScans} total scans`,
        status: 'completed',
        metrics: { 
          scans: campaign?.analytics?.totalScans, 
          conversions: campaign?.analytics?.totalConversions 
        }
      });
    }

    // Campaign completion milestone (if end date exists)
    if (campaign?.endDate && campaign?.status === 'completed') {
      milestones?.push({
        id: `${campaign?.id}-completed`,
        date: campaign?.endDate?.toISOString()?.split('T')?.[0],
        type: 'end',
        title: 'Campaign Completed',
        description: `${campaign?.name} campaign successfully completed`,
        status: 'completed',
        metrics: { 
          scans: campaign?.analytics?.totalScans || 0, 
          conversions: campaign?.analytics?.totalConversions || 0 
        }
      });
    }

    return milestones?.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Generate performance data from campaign analytics
  const generatePerformanceFromCampaign = (campaign) => {
    if (!campaign) return [];

    // Generate basic performance timeline based on campaign data
    const performance = [];
    const startDate = campaign?.startDate || campaign?.createdAt;
    const endDate = campaign?.endDate || new Date();
    
    // Generate 7 data points for the timeline
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date?.setDate(date?.getDate() + (i * Math.floor((endDate - startDate) / (7 * 24 * 60 * 60 * 1000))));
      
      performance?.push({
        date: date?.toISOString()?.split('T')?.[0],
        scans: Math.floor((campaign?.analytics?.totalScans || 0) * (i + 1) / 7),
        conversions: Math.floor((campaign?.analytics?.totalConversions || 0) * (i + 1) / 7),
        ctr: campaign?.analytics?.conversionRate || 0
      });
    }

    return performance;
  };

  // Generate events from campaign data
  const generateEventsFromCampaign = (campaign) => {
    if (!campaign) return [];

    const events = [];
    
    events?.push({
      id: `${campaign?.id}-event-created`,
      date: campaign?.createdAt?.toISOString()?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
      type: 'system',
      title: 'Campaign Created',
      description: 'Campaign setup completed',
      user: 'Campaign Manager'
    });

    if (campaign?.qrCodes?.length > 0) {
      events?.push({
        id: `${campaign?.id}-event-qrs`,
        date: campaign?.createdAt?.toISOString()?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
        type: 'qr',
        title: 'QR Codes Generated',
        description: `${campaign?.qrCodes?.length} QR codes created and deployed`,
        user: 'System'
      });
    }

    if (campaign?.status === 'completed') {
      events?.push({
        id: `${campaign?.id}-event-completed`,
        date: campaign?.endDate?.toISOString()?.split('T')?.[0] || new Date()?.toISOString()?.split('T')?.[0],
        type: 'completion',
        title: 'Campaign Completed',
        description: 'Final report generated and stakeholders notified',
        user: 'System'
      });
    }

    return events?.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'start': return 'Play';
      case 'milestone': return 'Flag';
      case 'optimization': return 'Zap';
      case 'alert': return 'AlertTriangle';
      case 'end': return 'CheckCircle';
      case 'system': return 'Settings';
      case 'qr': return 'QrCode';
      case 'update': return 'Edit';
      case 'completion': return 'Trophy';
      default: return 'Circle';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'start': return 'text-success bg-success/10';
      case 'milestone': return 'text-primary bg-primary/10';
      case 'optimization': return 'text-warning bg-warning/10';
      case 'alert': return 'text-error bg-error/10';
      case 'end': return 'text-success bg-success/10';
      case 'system': return 'text-muted-foreground bg-muted';
      case 'qr': return 'text-secondary bg-secondary/10';
      case 'update': return 'text-accent bg-accent/10';
      case 'completion': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US')?.format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get current campaign and generate timeline data
  const currentCampaign = campaigns?.find(c => c?.id === selectedCampaign);
  let timelineItems = [];

  if (currentCampaign) {
    switch (timelineView) {
      case 'milestones':
        timelineItems = generateMilestonesFromCampaign(currentCampaign);
        break;
      case 'performance':
        timelineItems = generatePerformanceFromCampaign(currentCampaign);
        break;
      case 'events':
        timelineItems = generateEventsFromCampaign(currentCampaign);
        break;
      default:
        timelineItems = [];
    }
  }

  if (isLoading || loading) {
    return (
      <div className={`bg-card border border-border rounded-lg ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading timeline...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Icon name="Timeline" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Campaign Timeline</h3>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Campaign Selector */}
            {campaigns?.length > 0 ? (
              <select
                value={selectedCampaign || ''}
                onChange={(e) => setSelectedCampaign(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm focus:ring-2 focus:ring-ring focus:border-transparent"
              >
                <option value="">Select Campaign</option>
                {campaigns?.map((campaign) => (
                  <option key={campaign?.id} value={campaign?.id}>
                    {campaign?.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="px-3 py-2 border border-border rounded-md bg-muted text-muted-foreground text-sm">
                No campaigns available
              </div>
            )}

            {/* View Toggle */}
            <div className="flex bg-muted rounded-lg p-1">
              {[
                { key: 'milestones', label: 'Milestones', icon: 'Flag' },
                { key: 'performance', label: 'Performance', icon: 'TrendingUp' },
                { key: 'events', label: 'Events', icon: 'Calendar' }
              ]?.map((view) => (
                <Button
                  key={view?.key}
                  variant={timelineView === view?.key ? 'default' : 'ghost'}
                  size="sm"
                  iconName={view?.icon}
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => setTimelineView(view?.key)}
                  className="text-xs"
                >
                  {view?.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <Icon name="AlertTriangle" size={20} className="text-yellow-600 mr-3" />
              <p className="text-yellow-800">{error}</p>
            </div>
          </div>
        )}

        {timelineView === 'milestones' && (
          <div className="space-y-6">
            {timelineItems?.map((item, index) => (
              <div key={item?.id} className="relative">
                {/* Timeline Line */}
                {index < timelineItems?.length - 1 && (
                  <div className="absolute left-6 top-12 w-0.5 h-16 bg-border"></div>
                )}
                
                <div className="flex gap-4">
                  {/* Timeline Icon */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getTypeColor(item?.type)}`}>
                    <Icon name={getTypeIcon(item?.type)} size={20} />
                  </div>
                  
                  {/* Timeline Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">{item?.title}</h4>
                      <span className="text-sm text-muted-foreground">{formatDate(item?.date)}</span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">{item?.description}</p>
                    
                    {item?.metrics && (
                      <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Icon name="Eye" size={14} className="text-muted-foreground" />
                          <span className="text-foreground">{formatNumber(item?.metrics?.scans)} scans</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Target" size={14} className="text-muted-foreground" />
                          <span className="text-foreground">{formatNumber(item?.metrics?.conversions)} conversions</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {timelineView === 'performance' && (
          <div className="space-y-4">
            {timelineItems?.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                <div>
                  <div className="font-medium text-foreground">{formatDate(item?.date)}</div>
                  <div className="text-sm text-muted-foreground">Performance snapshot</div>
                </div>
                <div className="flex gap-6 text-sm">
                  <div className="text-center">
                    <div className="font-medium text-foreground">{formatNumber(item?.scans)}</div>
                    <div className="text-muted-foreground">Scans</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-foreground">{formatNumber(item?.conversions)}</div>
                    <div className="text-muted-foreground">Conversions</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-foreground">{item?.ctr?.toFixed(1)}%</div>
                    <div className="text-muted-foreground">CTR</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {timelineView === 'events' && (
          <div className="space-y-4">
            {timelineItems?.map((item) => (
              <div key={item?.id} className="flex gap-4 p-4 bg-muted/20 rounded-lg">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTypeColor(item?.type)}`}>
                  <Icon name={getTypeIcon(item?.type)} size={16} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-foreground">{item?.title}</h4>
                    <span className="text-sm text-muted-foreground">{formatDate(item?.date)}</span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2">{item?.description}</p>
                  
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="User" size={12} />
                    <span>by {item?.user}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {timelineItems?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="font-medium text-foreground mb-2">No Timeline Data</h4>
            <p className="text-sm text-muted-foreground">
              {campaigns?.length === 0 
                ? "Create campaigns to see timeline data here."
                : `No ${timelineView} data available for the selected campaign.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignTimeline;